import React, { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import ShortUniqueId from 'short-unique-id';
import {
  addCalculationField,
  addConditionFilter,
} from '../../../redux/Calculations/CalculationsActions';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { isFlowFilledProperly, isFlowAlreadyPresentInStep } from '../utils';
type TDroppedItem = {
  type: 'card' | 'calculationSelected';
  dragFrom: string;
  uid: string;
  bIndex?: number;
  boxUUID: string | null;
  name: string;
  fieldData: {
    tableId?: string;
    fieldname: string;
    displayname: string;
    dataType: string;
    isAggregated?: boolean;
  };
};
const EvaluationDropZone = ({
  children,
  disabled,
  message,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  message?: string;
}) => {
  const [alert, setAlert] = useState<any>(null);
  const [showBorder, setShowBorder] = useState(false);

  const dispatch = useDispatch();
  const {
    selectedTabId,
    selectedTileId,
    calculations: _calculations,
  } = useSelector((state: RootState) => ({
    selectedTabId: state.tabTileProps.selectedTabId,
    selectedTileId: state.tabTileProps.selectedTileId,
    calculations: state.calculations,
  }));
  const propKey = `${selectedTabId}.${selectedTileId}`;
  const currentCalculationSession = _calculations.properties[propKey].currentCalculationSession;
  const uid = new ShortUniqueId({ length: 4 });
  const conditionNumber = currentCalculationSession?.activeCondition;
  const selectedFlow =
    currentCalculationSession?.calculationInfo.flows[currentCalculationSession?.activeFlow!][
      conditionNumber ?? 0
    ];

  const isDropNotAllowed = (draggedFlowName: string, activeFlowName: string): boolean =>
    draggedFlowName === activeFlowName;
  const [{ isOver }, dropEvaluation] = useDrop({
    accept: ['card', 'step', 'calculation', 'calculationSelected'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
    canDrop: () => selectedFlow?.condition !== 'else',
    drop(item: TDroppedItem, monitor) {
      setShowBorder(false);
      if (disabled) {
        setAlert({
          severity: 'warning',
          message:
            message ??
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      if (monitor.didDrop()) return;
      if (['calculation', 'step'].includes(item.type)) {
        setAlert({
          severity: 'warning',
          message: 'Using another calculated field as a source feature is coming in future release',
        });
        return;
      }
      if (item.type !== 'calculationSelected' && !item.fieldData) return;

      /**
       * if item.type===card -> dropped item is a field i.e column of table
       */
      if (!currentCalculationSession) return;
      const actFlow = currentCalculationSession.activeFlow!;
      if (item.type === 'calculationSelected') {
        const isAgggregated = currentCalculationSession.flowAggregations[actFlow];
        if (isAgggregated === null || isAgggregated === undefined) {
          setAlert({
            severity: 'warning',
            message: 'Please select an aggregation type for this flow before dropping a source',
          });
        }
        const droppedItemAggregationType = item.fieldData.isAggregated;
        if (
          (isAgggregated && !droppedItemAggregationType) ||
          (droppedItemAggregationType && !isAgggregated)
        ) {
          setAlert({
            severity: 'warning',
            message: 'Cannot drop a non-aggregated flow into an aggregated flow and vice versa',
          });
          return;
        }
      }
      if (isDropNotAllowed(item.uid, actFlow)) return;
      if (item.type === 'calculationSelected') {
        if (!isFlowFilledProperly(currentCalculationSession.calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }
        const dropZoneType = 'condition';
        if (
          isFlowAlreadyPresentInStep(
            currentCalculationSession.calculationInfo,
            item.uid,
            actFlow,
            conditionNumber ?? 0,
            dropZoneType,
          )
        ) {
          const droppedFlowName = currentCalculationSession?.flowDisplayNames?.[item.uid];
          const parentFlowName = currentCalculationSession?.flowDisplayNames?.[actFlow];
          setAlert({
            severity: 'warning',
            message: `${droppedFlowName} is already present inside ${parentFlowName}.`,
          });
          return;
        }
      }
      const uId = uid();
      if (!uId) return;

      const itemDataType = item.fieldData.dataType;

      let flowResultantDatatype = null;

      if (item.type === 'calculationSelected') {
        const flowName = currentCalculationSession.calculationInfo.flows[item.uid][0]?.flow;
        flowResultantDatatype =
          flowName === 'IfElse'
            ? currentCalculationSession.calculationInfo.selectedResultType[item.uid]
            : currentCalculationSession.calculationInfo.flows[item.uid][0]?.resultantDatatype;
      }

      const activeConditionIndex = currentCalculationSession?.activeCondition;
      const activeFlow = currentCalculationSession?.activeFlow;
      if (!activeFlow || activeConditionIndex == null) return;

      let aggregationNotMatched;

      if (
        item.type === 'calculationSelected' &&
        currentCalculationSession?.calculationInfo?.flows[activeFlow][activeConditionIndex]
          .isAggregation
      ) {
        // we will be able to drop only aggregated flows
        if (!currentCalculationSession.calculationInfo.flows[item.uid][0].isAggregation) {
          aggregationNotMatched = true;
        }
      } else if (
        item.type === 'calculationSelected' &&
        !currentCalculationSession?.calculationInfo?.flows[activeFlow][activeConditionIndex]
          .isAggregation
      ) {
        if (currentCalculationSession?.calculationInfo?.flows[item.uid][0].isAggregation) {
          aggregationNotMatched = true;
        }
      }

      if (aggregationNotMatched) {
        setAlert({
          severity: 'warning',
          message:
            "Aggregation of the flow is not matching with the selected condition's aggregation.",
        });
        return;
      }

      // basically we are assuming card to be a field
      if (item.type === 'card') {
        dispatch(
          addCalculationField(propKey, uId, {
            tableId: item.fieldData.tableId!,
            fieldName: item.fieldData.fieldname,
            displayName: item.fieldData.displayname,
            dataType: item.fieldData.dataType,
          }),
        );
      }

      if (
        ((item.type === 'card' || item.type === 'calculationSelected') &&
          itemDataType === 'date') ||
        itemDataType === 'timestamp' ||
        flowResultantDatatype === 'date' ||
        flowResultantDatatype === 'timestamp'
      ) {
        dispatch(
          addConditionFilter(propKey, {
            conditionUID: uid(),
            filterType: 'Pick List',
            leftOperand: [item.type === 'card' ? uId : item.uid],
            leftOperandType: [item.type === 'card' ? 'field' : 'flow'],
            operator: 'in',
            rightOperand: [],
            rightOperandType: [],
            shouldExclude: false,
            isTillDate: false,
            relativeCondition: null,
            timeGrain: 'year',
            isValid: true,
            parameterId: [],
          }),
        );
      } else {
        dispatch(
          addConditionFilter(propKey, {
            conditionUID: uid(),
            filterType: ['integer', 'decimal', 'float'].includes(
              item.type === 'card' ? itemDataType! : flowResultantDatatype!,
            )
              ? 'Search Condition'
              : 'Pick List',
            leftOperand: [item.type === 'card' ? uId : item.uid], // tobe modified later based on other dropped itemn type
            leftOperandType: [item.type === 'card' ? 'field' : 'flow'], // tobe modified later based on other dropped itemn type
            operator: ['integer', 'decimal', 'float'].includes(
              item.type === 'card' ? itemDataType! : flowResultantDatatype!,
            )
              ? 'greaterThan'
              : 'in',
            rightOperand: [],
            rightOperandType: [],
            shouldExclude: false,
            isTillDate: false,
            relativeCondition: null,

            /**Checkmark */
            isValid: ['integer', 'decimal', 'float'].includes(
              item.type === 'card' ? itemDataType! : flowResultantDatatype!,
            )
              ? false
              : true,
            parameterId: [],
          }),
        );
      }
    },
  });

  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);

  return (
    <div
      ref={dropEvaluation}
      data-testid="evaluation-dropzone"
      style={{
        cursor: 'pointer',
        margin: '0',
        textAlign: 'left',
        paddingBottom: '10px',
        paddingTop: '5px',
        minHeight: '8rem',
        height: '100%',
        // backgroundColor:'red',
        paddingLeft: '0.5rem',
        marginRight: '0.4rem',
        border: '1px solid transparent',
      }}
      className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
    >
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      {children}
    </div>
  );
};

export default EvaluationDropZone;
